import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDropzone } from "react-dropzone";
import { toast } from "react-toastify";
import Lottie from "lottie-react";
import {
  getSPDocumentsForId,
  getSPDocumentLibraries,
  sharePoint,
} from "../services/soar";
import ExpandDiv from "../common/ExpandDiv";
import SearchBox from "../common/SearchBox";
import FolderChangeModal from "./FolderChangeModal";
import DeleteFileModal from "./DeleteFileModal";
import FloatingInput from "../common/FloatingInput";
import CreateFolderModal from "./CreateFolderModal";
import * as fileloading from "../../animations/file-folder.json";

function Documents({ id, type, state, puconfig, prevLocation }) {
  const [docs, setDocs] = useState([]);
  const [fileTypes, setFileTypes] = useState([]);
  const isMountedRef = useRef(true);
  const [updateDocuments, setUpdateDocuments] = useState(true);
  const [showDeleteRequest, setShowDeleteRequest] = useState(false);
  const [showFolderChange, setFolderChange] = useState(false);
  const [showFolderCreate, setShowFolderCreate] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortyBy, setSortBy] = useState("name");
  const [loading, setLoading] = useState(false);
  const [uploadFolder, SetUploadFolder] = useState("");

  const sortOptions = [
    { _id: "name", name: "Name A-Z" },
    { _id: "newest", name: "Newest First" },
    { _id: "oldest", name: "Oldest First" },
  ];

  //TODO add other states when available
  let stateId = "UNDEFINED";
  switch (state) {
    case "Alabama":
      stateId = "c368c3e0-c87d-4d51-b5ed-f894369e1c8c";
      break;
    case "Colorado":
      stateId = "abd4b01a-7c5e-480a-8213-23389b66c7d6";
      break;
    case "Indiana":
      stateId = "ba2806a8-fe1d-4879-9782-b35e5b0eb753";
      break;
    case "Kentucky":
      stateId = "bf2cbc03-0197-41ab-a8ec-36ca850a9332";
      break;
    case "Nevada":
      stateId = "3bb131a9-f005-4701-9f77-2127674e4480";
      break;
    case "Ohio":
      stateId = "45e3e81e-bf39-4285-9216-3186282b2dfc";
      break;
    case "South Carolina":
      stateId = "f7c983cd-c291-45d5-b8e0-c0b10cd50ee6";
      break;
    default:
      break;
  }

  //get documents and folders
  useEffect(() => {
    getDocuments();
    setUpdateDocuments(false);
  }, [updateDocuments, puconfig]);

  const getDocuments = () => {
    // getSPDocumentLibraries().then((data) => {
    //   console.log("DocumentLibraries", data);
    // });
    const controller = new AbortController();
    const signal = controller.signal;
    setLoading(true);
    getSPDocumentsForId(id, type, stateId, signal).then((data) => {
      let resp = data.sort((a, b) => {
        if (a.createdDateTime < b.createdDateTime) return -1;
        if (a.createdDateTime > b.createdDateTime) return 1;
        return 0;
      });

      setDocs(resp);
      setFileTypes([
        ...new Set([
          ...(puconfig.documentFolders || []),
          ...resp.map((doc) => doc.fields.FolderType),
        ]),
      ]);
      setLoading(false);
    });
  };

  //get Dcoument Icon based on file extension
  const docIcon = (item) => {
    const ext = item.fields.DocIcon;
    switch (ext) {
      case "pdf":
        return (
          <i className="far fa-2x fa-file-pdf text-danger me-2 ms-0 ps-0" />
        );
      case "doc":
      case "docx":
        return <i className="far fa-2x fa-file-word text-primary me-2" />;
      case "xls":
      case "xlsx":
        return <i className="far fa-2x fa-file-excel text-success me-2" />;
      case "ppt":
      case "pptx":
        return <i className="far fa-2x fa-file-powerpoint text-danger me-2" />;
      default:
        return <i className="far fa-2x fa-file text-dark me-2" />;
    }
  };

  // On Drop of file, process and upload it to sharepoint
  const onDrop = useCallback(
    (acceptedFiles, rejectedFiles, event) => {
      console.log("On Drop", event, uploadFolder, acceptedFiles);
      const toastMessage =
        acceptedFiles.length > 1
          ? `Uploading File 1 of ${acceptedFiles.length}...`
          : "Uploading File...";
      toast.info(toastMessage, { progress: 0 });

      acceptedFiles.forEach(async (file, index) => {
        console.log("Event", event, uploadFolder);
        try {
          console.log("Index", index, acceptedFiles.length);
          const reader = new FileReader();
          reader.onabort = () => console.log("file reading was aborted");
          reader.onerror = () => console.log("file reading has failed");
          reader.onload = async () => {
            // console.log("File Stream", reader.result.split(","));
            const body = {
              Command: "upload",
              LibraryId: stateId,
              Field: type,
              Value: id,
              FileName: file.name,
              FileData: await reader.result.split(",")[1],
              FolderName: event
                ? event.target.id.replace("-upload", "")
                : uploadFolder.replace("-upload", ""),
            };

            const response = await sharePoint(body);

            if (response.code === "success") {
              toast.success(
                `File ${response.data["file-name"]} uploaded successfully...`
              );
              //setUpdateDocuments(true);
              if (index === acceptedFiles.length - 1) {
                setUpdateDocuments(true);
              }
            } else {
              toast.error(`Error uploading file... ${response.message}`);
            }
          };
          reader.readAsDataURL(file);
        } catch (error) {
          console.log("Error reading file:", error);
          toast.error("Error reading file...");
        }
      });

      if (rejectedFiles.length > 0) {
        toast.error(
          <div>
            <div>
              <b>{`Rejected Files (${rejectedFiles[0].errors[0].code})`}</b>
            </div>
            <div>
              {rejectedFiles.map((file) => (
                <div key={file.file.name}>{file.file.name}</div>
              ))}
            </div>
          </div>
        );
      }
    },
    [uploadFolder]
  );

  // Set accepted file mime types and extensions
  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      "image/png": [".png"],
      "image/jpeg": [".jpg", ".jpeg"],
      "application/pdf": [".pdf"],
      "application/msword": [".doc"],
      "application/msword": [".docx"],
      "text/csv": [".csv"],
      "application/vnd.ms-excel": [".xls"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "application/vnd.oasis.opendocument.text": [".odt"],
    },
  });

  return loading === true ? (
    <Lottie
      className="w-25"
      height={120}
      width={120}
      loop={true}
      autoplay={true}
      animationData={fileloading.default}
      rendererSettings={{
        preserveAspectRatio: "xMidYMid slice",
      }}
    />
  ) : (
    <>
      <CreateFolderModal
        show={showFolderCreate}
        availableFolders={fileTypes}
        setAvailableFolders={setFileTypes}
        handleClose={() => setShowFolderCreate(false)}
      />
      <FolderChangeModal
        show={showFolderChange}
        item={selectedItem}
        stateId={stateId}
        currentFolder={selectedFolder}
        availableFolders={fileTypes}
        handleClose={() => {
          setSelectedItem(null);
          setFolderChange(false);
          setUpdateDocuments(true);
        }}
      />
      <DeleteFileModal
        show={showDeleteRequest}
        item={selectedItem}
        state={state}
        handleClose={() => {
          setSelectedItem(null);
          setShowDeleteRequest(false);
          setUpdateDocuments(true);
        }}
      />
      <div>
        <div className="h5 text-danger mb-2">
          New Documents will be uploaded, and displayed, within the Folder
          listed below
        </div>
        {prevLocation && Object.keys(prevLocation).length > 0 && (
          <div className="h5 mb-4">
            Previously uploaded documents can be accessed clicking{" "}
            <span
              className="text-primary"
              role="button"
              onClick={() =>
                window.open(
                  prevLocation["doc.absoluteurl"]
                    ? prevLocation["doc.absoluteurl"]
                    : `https://soardocs.safy.org/${
                        type === "ClientID" ? "contact" : "ccx_fostercarehome"
                      }/${prevLocation["doc.relativeurl"]}`
                )
              }
            >
              Here
            </span>
          </div>
        )}
      </div>
      <div className="row mb-4">
        <div className="col-6 pt-1">
          <SearchBox
            className="rounded border border-2 border-info"
            noBorder={true}
            style={{ height: "55px" }}
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="col-3">
          <FloatingInput
            className="border-2 border-info"
            type="select"
            name="sort"
            label="Sort By"
            value={sortyBy}
            onChange={(e) => setSortBy(e.target.value)}
            options={sortOptions}
          />
        </div>
      </div>
      <div className="row">
        <div className="col-3 h4">Folders</div>
        <div className="col-7"></div>
        <div className="col-2">
          <div
            className="btn btn-primary float-right"
            onClick={() => setShowFolderCreate(true)}
          >
            Add Folder
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-6">
          {fileTypes?.map((type, index) => (
            <ExpandDiv
              key={`${type}=${index}`}
              title={type}
              expandIcon="fas fa-folder fa-xl text-warning"
              collapseIcon="fas fa-folder-open fa-xl text-warning"
              expandStatus={
                searchQuery !== "" &&
                docs.filter(
                  (d) =>
                    d.fields.FolderType === type &&
                    d.fields.LinkFilename.toLowerCase().includes(
                      searchQuery.toLowerCase()
                    )
                ).length > 0
              }
            >
              {docs
                .filter(
                  (d) =>
                    d.fields.FolderType === type &&
                    d.fields.LinkFilename.toLowerCase().includes(
                      searchQuery.toLowerCase()
                    )
                )
                .sort((a, b) => {
                  switch (sortyBy) {
                    case "name":
                      return a.fields.LinkFilename.localeCompare(
                        b.fields.LinkFilename
                      );
                    case "newest":
                      return (
                        new Date(b.createdDateTime) -
                        new Date(a.createdDateTime)
                      );
                    case "oldest":
                      return (
                        new Date(a.createdDateTime) -
                        new Date(b.createdDateTime)
                      );
                    default:
                      return a.fields.LinkFilename.localeCompare(
                        b.fields.LinkFilename
                      );
                  }
                })
                // .sort((a, b) =>
                //   a.fields.LinkFilename.localeCompare(b.fields.LinkFilename)
                // )
                .map((d, fileIndex) => (
                  <div
                    className="row rounded border bg-light card-safy mb-1 ms-4"
                    key={`File ${fileIndex}`}
                  >
                    <div
                      className="col-10 ps-0 "
                      onClick={() => window.open(`${d.webUrl}?web=1`)}
                      role="button"
                      title={`Created: ${new Date(
                        d.createdDateTime
                      ).toLocaleDateString()} Last Modified: ${new Date(
                        d.lastModifiedDateTime
                      ).toLocaleDateString()} Last Modified By: ${
                        d.lastModifiedBy?.user?.displayName
                      }`}
                    >
                      {docIcon(d)}
                      {d.fields.LinkFilename}
                    </div>
                    <div className="col-2 mt-1">
                      {(JSON.parse(
                        localStorage.getItem("safyInfo")
                      ).user.modules.includes("Document Management") ||
                        JSON.parse(
                          localStorage.getItem("safyInfo")
                        ).user.modules.includes("Admin")) && (
                        <i
                          className="fas fa-folder-tree text-primary mx-2"
                          title="Change Folder"
                          role="button"
                          onClick={() => {
                            setSelectedItem(d);
                            setSelectedFolder(d.fields.FolderType);
                            setFolderChange(true);
                          }}
                        />
                      )}
                      <i
                        className="fas fa-trash text-danger"
                        title="Request to Delete"
                        role="button"
                        onClick={() => {
                          setSelectedItem(d);
                          setShowDeleteRequest(true);
                        }}
                      />
                    </div>
                  </div>
                ))}
              <div className="row ms-4">
                <div
                  className="col-12 rounded border border-dark p-2 mb-4 mt-2 bg-safy3-1 text-dark"
                  {...getRootProps()}
                  id={`${type}-upload`}
                  onFocus={() => {
                    console.log("SettingFocus", type);
                    SetUploadFolder(type);
                  }}
                >
                  <input
                    {...getInputProps({ folder: type })}
                    id={`${type}-upload`}
                    onFocus={() => {
                      console.log("SettingFocus", type);
                      SetUploadFolder(type);
                    }}
                  />
                  <p
                    className="fw-bold m-4 p-0 text-center"
                    id={`${type}-upload`}
                    onFocus={() => {
                      console.log("SettingFocus", type);
                      SetUploadFolder(type);
                    }}
                  >
                    {`Drag files here, or click here to select files to ${type} folder`}
                  </p>
                </div>
              </div>
            </ExpandDiv>
          ))}
        </div>
      </div>
    </>
  );
}

export default Documents;
